import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { orderToken } from "@/lib/orderToken";
import { formatMoney } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { tableStore } from "@/lib/cart";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CheckCircle2, ChefHat, Bell, Home, Star } from "lucide-react";
import { toast } from "sonner";

const STAGES = [
  { key: "received", label: "Received", icon: CheckCircle2 },
  { key: "preparing", label: "Preparing", icon: ChefHat },
  { key: "ready", label: "Ready", icon: Bell },
  { key: "fulfilled", label: "Served", icon: Home },
];

export default function OrderTracking() {
  const { orderId } = useParams();
  const auth = useAuth();
  const [order, setOrder] = useState(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const tbl = tableStore.get();

  useEffect(() => {
    const cfg = { headers: { "X-Order-Token": orderToken.get(orderId) } };
    const load = () => api.get(`/orders/${orderId}`, cfg)
      .then((r) => setOrder(r.data))
      .catch((e) => {
        // Only a genuine access/exists failure is "denied". A transient blip (5xx,
        // network) must NOT wipe a live order — keep the last-good view and retry.
        const s = e?.response?.status;
        if (s === 401 || s === 403 || s === 404) setOrder((prev) => (prev && prev !== "denied") ? prev : "denied");
      });
    load();
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [orderId]);

  if (order === "denied") return (
    <div className="min-h-screen grid place-items-center p-8 text-center">
      <div className="max-w-sm w-full">
        <p className="font-display text-xl font-bold mb-1">Order not available</p>
        <p className="text-sm text-[var(--qd-muted)] mb-6">Open this from the device you ordered on, or sign in with the account you used.</p>
        <div className="flex flex-col gap-2">
          {!auth && <Link to="/login"><Button className="w-full h-12 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Sign in to view your orders</Button></Link>}
          {tbl
            ? <Link to={`/m/${tbl.token}`}><Button variant="outline" className="w-full h-12 rounded-full">Back to menu</Button></Link>
            : <Link to="/"><Button variant="outline" className="w-full h-12 rounded-full">QRDine home</Button></Link>}
        </div>
      </div>
    </div>
  );

  if (!order) return <TrackSkeleton />;

  const cancelled = order.status === "cancelled";
  const done = order.status === "completed";
  const stageIdx = STAGES.findIndex((s) => s.key === (done ? "fulfilled" : order.status));
  const money = (v) => formatMoney(v);
  const parsedAt = Date.parse(order.created_at);
  const placedMin = Number.isFinite(parsedAt) ? Math.max(0, Math.floor((Date.now() - parsedAt) / 60000)) : null;
  const placedAgo = placedMin === null ? null
    : placedMin < 1 ? "just now"
    : placedMin < 60 ? `${placedMin} min ago`
    : placedMin < 48 * 60 ? `${Math.floor(placedMin / 60)}h ${placedMin % 60}m ago`
    : `${Math.floor(placedMin / 1440)} days ago`;

  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain pb-24">
      <header className="p-5">
        {tbl
          ? <Link to={`/m/${tbl.token}`} data-testid="track-back-menu" className="text-sm font-semibold text-[var(--qd-primary)] inline-flex items-center gap-1">← Back to menu</Link>
          : <Link to="/" className="text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)]">← Home</Link>}
      </header>
      <div className="px-5">
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-white rounded-3xl p-6 qd-shadow-soft border border-[var(--qd-border)]">
          <div className="text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)]">Order</div>
          <div className="font-display font-black text-3xl mt-1">#{(order.id || "").slice(0, 6).toUpperCase()}</div>
          <div className="text-sm text-[var(--qd-muted)] mt-1">{order.table_label}{placedAgo ? ` · placed ${placedAgo}` : ""}</div>

          {cancelled ? (
            <div data-testid="order-cancelled-banner" className="mt-6 rounded-2xl bg-red-50 border border-red-200 p-4 text-sm text-red-700">
              <div className="font-semibold">This order was cancelled.</div>
              <div className="mt-1 text-red-600/80">Any redeemed points have been returned. Ask your waiter if this looks wrong.</div>
            </div>
          ) : (
            <>
              {done && (
                <div data-testid="order-done-banner" className="mt-6 rounded-2xl bg-green-50 border border-green-200 p-3 text-sm text-green-700 font-semibold">
                  Served &amp; paid — thank you! 🎉
                </div>
              )}
              {/* Progress */}
              <div className="mt-6 grid grid-cols-4 gap-2">
                {STAGES.map((s, i) => {
                  const active = i <= stageIdx;
                  const current = i === stageIdx && !done;
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className="flex flex-col items-center text-center gap-2" data-testid={`stage-${s.key}`}>
                      <div className={`w-10 h-10 rounded-full grid place-items-center transition-colors ${active ? "bg-[var(--qd-primary)] text-white" : "bg-[var(--qd-cream)] text-[var(--qd-muted)]"} ${current ? "animate-pulse" : ""}`}>
                        <Icon className="w-4 h-4" aria-hidden />
                      </div>
                      <span className={`text-[10px] font-mono-alt uppercase tracking-widest ${active ? "text-[var(--qd-text)]" : "text-[var(--qd-muted)]"}`}>{s.label}</span>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 h-1 bg-[var(--qd-cream)] rounded-full overflow-hidden">
                <motion.div className="h-full bg-[var(--qd-primary)]" initial={{ width: 0 }} animate={{ width: `${((stageIdx + 1) / STAGES.length) * 100}%` }} transition={{ duration: 0.6 }} />
              </div>
            </>
          )}
        </motion.div>

        {/* Items */}
        <div className="mt-4 bg-white rounded-3xl p-5 border border-[var(--qd-border)]">
          <div className="text-xs font-mono-alt uppercase tracking-widest text-[var(--qd-muted)] mb-3">Items</div>
          <div className="space-y-2">
            {(order.items || []).map((it, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span>
                  {it.quantity} × {it.name}
                  {it.modifiers && it.modifiers.length > 0 && <span className="block text-xs text-[var(--qd-muted)]">{it.modifiers.map((m) => m.option_name).join(", ")}</span>}
                  {it.notes && <span className="block text-xs text-[var(--qd-muted)] italic">“{it.notes}”</span>}
                </span>
                <span className="font-semibold whitespace-nowrap">{money(it.line_total)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[var(--qd-border)] mt-3 pt-3 space-y-1.5 text-sm">
            <Row label="Subtotal" val={money(order.subtotal)} />
            {order.discount > 0 && <Row label={order.coupon_code ? `Coupon · ${order.coupon_code}` : "Discount"} val={`−${money(order.discount)}`} accent />}
            {order.loyalty_discount > 0 && <Row label="Loyalty" val={`−${money(order.loyalty_discount)}`} accent />}
            {order.tax_mode !== "inclusive" && order.tax > 0 && <Row label={`GST${order.tax_percent ? ` (${order.tax_percent}%)` : ""}`} val={money(order.tax)} />}
            <div className="flex justify-between font-display font-bold pt-1">
              <span>Total</span>
              <span data-testid="order-total">{money(order.total)}</span>
            </div>
            {order.tax_mode === "inclusive" && order.tax > 0 && (
              <div className="text-right text-xs text-[var(--qd-muted)]">incl. GST {money(order.tax)}{order.tax_percent ? ` (${order.tax_percent}%)` : ""}</div>
            )}
          </div>
          <div className="mt-3 text-xs text-[var(--qd-muted)]">
            Payment: <span className="text-[var(--qd-text)] font-semibold uppercase">{order.payment_method === "counter" ? "Pay at Counter" : "Online · UPI"}</span> · {order.payment_status}
          </div>
          {order.payment_status !== "paid" && order.status !== "cancelled" && (
            <UpiPay orderId={orderId} method={order.payment_method} />
          )}
          {order.loyalty_points_earned > 0 && order.status === "completed" && (
            <div className="mt-3 rounded-xl bg-[var(--qd-cream)] p-3 text-xs">
              🎉 You earned <span className="font-bold">{order.loyalty_points_earned} points</span>
            </div>
          )}
        </div>

        {/* Review CTA */}
        {order.status === "completed" && auth?.user?.id && order.customer_id === auth.user.id && (
          <Button data-testid="open-review-btn" onClick={() => setReviewOpen(true)} className="w-full mt-4 h-12 rounded-full bg-[var(--qd-text)] hover:bg-black text-white">
            <Star className="w-4 h-4 mr-2" aria-hidden /> Rate your experience
          </Button>
        )}

        {/* Order more — keep the dine-in loop open (drinks → food → dessert). */}
        {tbl && !cancelled && (
          <Link to={`/m/${tbl.token}`} data-testid="order-more" className="block mt-4">
            <Button variant="outline" className="w-full h-12 rounded-full border-[var(--qd-primary)] text-[var(--qd-primary)] hover:bg-[var(--qd-primary)] hover:text-white">+ Order more</Button>
          </Link>
        )}
      </div>

      <ReviewDialog open={reviewOpen} onClose={() => setReviewOpen(false)} orderId={orderId} />
    </div>
  );
}

// UPI collect: shows the restaurant's payment QR (amount + note baked in) and a
// "pay with any app" intent link. Payment isn't auto-confirmed (no gateway) —
// tapping "I've paid" just flags the order so staff check their UPI app.
function UpiPay({ orderId, method }) {
  const [pay, setPay] = useState(null);
  const [claimed, setClaimed] = useState(false);
  const [busy, setBusy] = useState(false);
  const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
  const tok = orderToken.get(orderId);
  const cfg = { headers: { "X-Order-Token": tok } };

  useEffect(() => {
    api.get(`/orders/${orderId}/payment`, cfg).then((r) => { setPay(r.data); setClaimed(!!r.data.payment_claimed); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  if (!pay) return null;
  if (!pay.configured) {
    // Chose counter + no online option → nothing to add. Chose online but the
    // restaurant hasn't set up UPI → tell them to pay at the counter.
    return method === "online"
      ? <div className="mt-3 rounded-xl bg-[var(--qd-cream)] p-3 text-xs">Online payment isn't set up yet — please pay at the counter.</div>
      : null;
  }
  // A counter order can still choose to pay online now — offer it, don't force it.
  const heading = method === "counter" ? "Prefer to pay online now?" : `Pay ${formatMoney(pay.amount, pay.currency)} via UPI`;

  const iPaid = async () => {
    setBusy(true);
    try { await api.post(`/orders/${orderId}/payment/claim`, {}, cfg); setClaimed(true); toast.success("Thanks! Staff will confirm your payment shortly."); }
    catch { toast.error("Couldn't record that — please try again"); }
    setBusy(false);
  };

  return (
    <div className="mt-4 rounded-2xl border border-[var(--qd-border)] p-4 text-center" data-testid="upi-pay">
      <div className="text-sm font-semibold mb-1">{heading}</div>
      <div className="text-xs text-[var(--qd-muted)] mb-3">{formatMoney(pay.amount, pay.currency)} to {pay.payee} · {pay.upi_id}</div>
      <img src={`${BACKEND}/api/orders/${orderId}/upi-qr.png?t=${encodeURIComponent(tok)}`} alt="UPI QR" className="w-52 h-52 mx-auto rounded-lg border border-[var(--qd-border)]" />
      <div className="text-[11px] text-[var(--qd-muted)] mt-2">Scan with any UPI app, or</div>
      <a href={pay.upi_uri} data-testid="upi-intent" className="mt-2 inline-flex items-center justify-center gap-2 w-full h-12 rounded-full bg-[var(--qd-primary)] text-white font-semibold">
        Pay with any UPI app
      </a>
      {claimed ? (
        <div className="mt-3 text-xs text-green-600 font-semibold">✓ You marked this paid — awaiting staff confirmation</div>
      ) : (
        <button data-testid="upi-ipaid" onClick={iPaid} disabled={busy} className="mt-3 w-full h-11 rounded-full border border-[var(--qd-border)] text-sm font-semibold text-[var(--qd-text)] hover:bg-[var(--qd-cream)] disabled:opacity-50">
          {busy ? "Recording…" : "I've paid"}
        </button>
      )}
    </div>
  );
}

function ReviewDialog({ open, onClose, orderId }) {
  const [food, setFood] = useState(5);
  const [service, setService] = useState(5);
  const [ambience, setAmbience] = useState(5);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      await api.post("/reviews", { order_id: orderId, food_rating: food, service_rating: service, ambience_rating: ambience, comment });
      toast.success("Thanks for your review!");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="review-dialog" className="rounded-3xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">How was it?</DialogTitle></DialogHeader>
        <div className="space-y-5 mt-2">
          <Stars label="Food" val={food} set={setFood} testid="review-food" />
          <Stars label="Service" val={service} set={setService} testid="review-service" />
          <Stars label="Ambience" val={ambience} set={setAmbience} testid="review-ambience" />
          <Textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your thoughts…" className="rounded-xl" />
          <Button data-testid="review-submit" onClick={submit} disabled={submitting} className="w-full h-12 rounded-full bg-[var(--qd-primary)] hover:bg-[var(--qd-primary-dark)]">Submit review</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Stars({ label, val, set, testid }) {
  return (
    <div>
      <div className="text-sm font-semibold mb-2">{label}</div>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`${testid}-${n}`} onClick={() => set(n)} aria-label={`${n} star${n > 1 ? "s" : ""}`} aria-pressed={n <= val} className="p-1">
            <Star className={`w-8 h-8 transition-colors ${n <= val ? "fill-[var(--qd-primary)] text-[var(--qd-primary)]" : "text-[var(--qd-border)]"}`} aria-hidden />
          </button>
        ))}
      </div>
    </div>
  );
}

// Page-shaped skeleton so the first paint isn't a text spinner.
function TrackSkeleton() {
  return (
    <div className="min-h-screen bg-[var(--qd-bg)] qd-grain animate-pulse p-5" data-testid="track-skeleton">
      <div className="h-4 w-16 bg-gray-200 rounded" />
      <div className="mt-4 bg-white rounded-3xl border border-[var(--qd-border)] p-6 space-y-4">
        <div className="h-3 w-14 bg-gray-200 rounded" />
        <div className="h-8 w-36 bg-gray-200 rounded" />
        <div className="grid grid-cols-4 gap-2 pt-4">
          {[1, 2, 3, 4].map((k) => <div key={k} className="w-10 h-10 bg-gray-200 rounded-full mx-auto" />)}
        </div>
        <div className="h-1 bg-gray-200 rounded-full" />
      </div>
      <div className="mt-4 bg-white rounded-3xl border border-[var(--qd-border)] p-5 space-y-3">
        {[1, 2, 3].map((k) => <div key={k} className="h-4 bg-gray-200 rounded" />)}
      </div>
    </div>
  );
}

function Row({ label, val, accent }) {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--qd-muted)]">{label}</span>
      <span className={accent ? "text-[var(--qd-primary)] font-semibold" : ""}>{val}</span>
    </div>
  );
}
